import React from 'react';
import { FileText, QrCode, Calendar, BarChart3, Users, CreditCard } from 'lucide-react';
import { Header } from '../components/Layout/Header';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';

const businessFeatures = [
  { id: 'invoicing', name: 'Invoicing', icon: FileText, color: 'bg-blue-100 text-blue-600' },
  { id: 'qr-payments', name: 'QR Payments', icon: QrCode, color: 'bg-green-100 text-green-600' },
  { id: 'recurring', name: 'Recurring Bills', icon: Calendar, color: 'bg-purple-100 text-purple-600' },
  { id: 'analytics', name: 'Analytics', icon: BarChart3, color: 'bg-orange-100 text-orange-600' },
  { id: 'customers', name: 'Customers', icon: Users, color: 'bg-pink-100 text-pink-600' },
  { id: 'payments', name: 'Payment Links', icon: CreditCard, color: 'bg-indigo-100 text-indigo-600' },
];

const recentInvoices = [
  { id: '1', client: 'Acme Corp', amount: 2500, status: 'paid', date: '2024-01-15' },
  { id: '2', client: 'Tech Solutions', amount: 1800, status: 'pending', date: '2024-01-14' },
  { id: '3', client: 'Design Studio', amount: 950, status: 'overdue', date: '2024-01-10' },
];

const stats = [
  { label: 'Monthly Revenue', value: '$12,450', change: '+15%' },
  { label: 'Active Customers', value: '48', change: '+8%' },
  { label: 'Pending Invoices', value: '7', change: '-12%' },
];

export const B2B: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header title="Business Services" showProfile={false} />
      
      {/* Stats Overview */}
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3 mb-6">
          {stats.map((stat, index) => (
            <Card key={index}>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                <p className="text-xs text-green-600">{stat.change}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Business Features */}
      <div className="px-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Tools</h3>
        <div className="grid grid-cols-3 gap-4">
          {businessFeatures.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card key={feature.id} hover className="text-center">
                <div className={`w-12 h-12 rounded-full ${feature.color} flex items-center justify-center mx-auto mb-2`}>
                  <Icon size={20} />
                </div>
                <p className="text-sm font-medium text-gray-900">{feature.name}</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-4 mb-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Button fullWidth variant="outline" className="justify-start">
              <FileText size={16} className="mr-2" />
              Create New Invoice
            </Button>
            <Button fullWidth variant="outline" className="justify-start">
              <QrCode size={16} className="mr-2" />
              Generate QR Payment
            </Button>
            <Button fullWidth variant="outline" className="justify-start">
              <Users size={16} className="mr-2" />
              Add New Customer
            </Button>
          </div>
        </Card>
      </div>

      {/* Recent Invoices */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Invoices</h3>
          <Button variant="ghost" size="sm">View All</Button>
        </div>
        
        <div className="space-y-3">
          {recentInvoices.map((invoice) => (
            <Card key={invoice.id} hover>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{invoice.client}</p>
                  <p className="text-sm text-gray-500">{invoice.date}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${invoice.amount}</p>
                  <p className={`text-sm capitalize ${
                    invoice.status === 'paid' ? 'text-green-600' : 
                    invoice.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {invoice.status}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};